import {useEffect, useMemo, useState} from 'react'
import Compressor from 'compressorjs'
import {BASE_URL} from '../../../api'
import config from '../../config'
import uploadFileSecurely from '../../helper/secureUpload'
const {axios} = config

// Profile photos (2026-10-07): listed practitioners who have no profile
// photo, so the app shows a blank silhouette for them. Pick a photo for
// each one and it's saved straight away and live in the app. "Show
// everyone" lists all listed practitioners so you can replace a photo too.
const TOWNS = {
  Sayulita_Nay: 'Sayulita',
  San_Miguel_De_Allende: 'San Miguel de Allende',
  'Puerto Escondido': 'Puerto Escondido',
  Tulum: 'Tulum',
}

const nameOf = (u) =>
  (u.businessName && u.businessName.trim()) ||
  [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
  '(no name)'

// Shrink big phone photos before upload (max 1200px, JPEG).
const shrink = (file) =>
  new Promise((resolve) => {
    new Compressor(file, {
      quality: 0.85,
      maxWidth: 1200,
      maxHeight: 1200,
      mimeType: 'image/jpeg',
      success: (out) => resolve(new File([out], file.name.replace(/\.\w+$/, '') + '.jpg', {type: 'image/jpeg'})),
      error: () => resolve(file),
    })
  })

const ProfilePhotos = () => {
  const [all, setAll] = useState(false)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [town, setTown] = useState('')
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState({}) // userId -> true
  const [done, setDone] = useState({}) // userId -> new url
  const [notice, setNotice] = useState(null)

  const load = () => {
    setLoading(true)
    axios
      .get(`${BASE_URL}/admin/profile-photos`, {params: all ? {all: 1} : {}})
      .then(({data}) => setRows(data.data || []))
      .catch(() => setNotice({type: 'danger', text: 'Could not load practitioners.'}))
      .finally(() => setLoading(false))
  }
  useEffect(load, [all])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return rows.filter(
      (u) =>
        (!town || u.primaryLocation === town) &&
        (!needle ||
          [nameOf(u), u.email, u.instagramHandle].filter(Boolean).join(' ').toLowerCase().includes(needle))
    )
  }, [rows, town, q])

  const missingCount = rows.filter((u) => !u.profileImageUrl && !done[u._id]).length

  const onPick = async (u, file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setNotice({type: 'danger', text: 'Please choose an image file (JPG or PNG).'})
      return
    }
    setBusy((b) => ({...b, [u._id]: true}))
    setNotice(null)
    try {
      const small = await shrink(file)
      const {fileUrl} = await uploadFileSecurely(small)
      await axios.put(`${BASE_URL}/admin/profile-photos/${u._id}`, {profileImageUrl: fileUrl})
      setDone((d) => ({...d, [u._id]: fileUrl}))
      setNotice({type: 'success', text: `Photo saved for ${nameOf(u)}. It's live in the app now.`})
    } catch (e) {
      setNotice({
        type: 'danger',
        text: `Could not save the photo for ${nameOf(u)}: ${e?.response?.data?.message || e.message}`,
      })
    } finally {
      setBusy((b) => ({...b, [u._id]: false}))
    }
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title flex-column'>
          <h3 className='fw-bold mb-1'>Profile photos</h3>
          <div className='text-muted fs-7'>
            {all
              ? 'Every listed practitioner. Choose a new photo to replace one.'
              : `${missingCount} listed practitioner${missingCount === 1 ? '' : 's'} with no profile photo. Choose a photo for each - it's saved and live in the app right away.`}
          </div>
        </div>
        <div className='card-toolbar gap-3 flex-wrap'>
          <input
            className='form-control form-control-solid w-200px'
            placeholder='Search name, email, IG'
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select className='form-select form-select-solid w-200px' value={town} onChange={(e) => setTown(e.target.value)}>
            <option value=''>All towns</option>
            {Object.entries(TOWNS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <label className='form-check form-switch form-check-custom form-check-solid'>
            <input className='form-check-input' type='checkbox' checked={all} onChange={(e) => setAll(e.target.checked)} />
            <span className='form-check-label'>Show everyone</span>
          </label>
        </div>
      </div>

      <div className='card-body pt-0'>
        {notice && <div className={`alert alert-${notice.type} py-3`}>{notice.text}</div>}
        {loading ? (
          <div className='text-muted py-10 text-center'>Loading…</div>
        ) : shown.length === 0 ? (
          <div className='text-muted py-10 text-center'>
            {all ? 'No practitioners match.' : 'Everyone listed has a profile photo. 🎉'}
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-4'>
              <thead>
                <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                  <th>Photo</th>
                  <th>Practitioner</th>
                  <th>Town</th>
                  <th>Find a photo</th>
                  <th className='text-end'>Upload</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((u) => {
                  const url = done[u._id] || u.profileImageUrl
                  const ig = (u.instagramHandle || '').replace(/^@/, '').trim()
                  return (
                    <tr key={u._id}>
                      <td>
                        {url ? (
                          <img src={url} alt='' className='rounded-circle' style={{width: 56, height: 56, objectFit: 'cover'}} />
                        ) : (
                          <div className='rounded-circle bg-light d-flex align-items-center justify-content-center text-muted' style={{width: 56, height: 56}}>
                            <i className='bi bi-person fs-2' />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className='fw-bold'>{nameOf(u)}</div>
                        <div className='text-muted fs-7'>{u.email || u.phone}</div>
                      </td>
                      <td>{TOWNS[u.primaryLocation] || u.primaryLocation || '-'}</td>
                      <td className='fs-7'>
                        {ig && (
                          <a href={`https://instagram.com/${ig}`} target='_blank' rel='noreferrer' className='me-3'>
                            @{ig}
                          </a>
                        )}
                        {u.website && (
                          <a href={u.website.startsWith('http') ? u.website : `https://${u.website}`} target='_blank' rel='noreferrer'>
                            Website
                          </a>
                        )}
                      </td>
                      <td className='text-end'>
                        <label className={`btn btn-sm ${done[u._id] ? 'btn-light-success' : 'btn-primary'} mb-0`}>
                          {busy[u._id] ? 'Saving…' : done[u._id] ? 'Saved ✓ Replace' : url ? 'Replace photo' : 'Choose photo'}
                          <input
                            type='file'
                            accept='image/*'
                            hidden
                            disabled={!!busy[u._id]}
                            onChange={(e) => {
                              onPick(u, e.target.files?.[0])
                              e.target.value = ''
                            }}
                          />
                        </label>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProfilePhotos
